import { format, parseISO } from 'date-fns';

/**
 * Centralized date-time utility for consistent timestamp formatting across the application.
 * 
 * All timestamps from the API are in UTC (ISO format with Z).
 * This utility converts them to the user's local timezone and formats them consistently.
 */

/**
 * Default date-time format: MMM dd, yyyy HH:mm
 * Example: Jan 15, 2024 14:30
 */
export const DEFAULT_DATETIME_FORMAT = 'MMM dd, yyyy HH:mm';

/**
 * Date-time format with seconds: MMM dd, yyyy HH:mm:ss
 * Example: Jan 15, 2024 14:30:45
 */
export const DATETIME_WITH_SECONDS_FORMAT = 'MMM dd, yyyy HH:mm:ss';

/**
 * Date only format: MMM dd, yyyy
 * Example: Jan 15, 2024
 */
export const DATE_ONLY_FORMAT = 'MMM dd, yyyy';

/**
 * Time only format: HH:mm
 * Example: 14:30
 */
export const TIME_ONLY_FORMAT = 'HH:mm';

/**
 * Full date-time format: MMMM dd, yyyy 'at' HH:mm
 * Example: January 15, 2024 at 14:30
 */
export const FULL_DATETIME_FORMAT = "MMMM dd, yyyy 'at' HH:mm";

/**
 * Formats a UTC timestamp to the user's local timezone with the default format.
 * 
 * @param utcTimestamp - ISO 8601 UTC timestamp string (e.g., "2024-01-15T14:30:00.000Z")
 * @param formatString - Optional custom format string (defaults to DEFAULT_DATETIME_FORMAT)
 * @returns Formatted date-time string in user's local timezone, or '-' if invalid
 * 
 * @example
 * formatDateTime("2024-01-15T14:30:00.000Z") // "Jan 15, 2024 14:30" (in user's timezone)
 * formatDateTime("2024-01-15T14:30:00.000Z", DATE_ONLY_FORMAT) // "Jan 15, 2024"
 */
export function formatDateTime(
  utcTimestamp: string | null | undefined,
  formatString: string = DEFAULT_DATETIME_FORMAT
): string {
  if (!utcTimestamp) {
    return '-';
  }

  try {
    // Parse the ISO string (handles UTC automatically)
    const date = parseISO(utcTimestamp);
    
    // Format to user's local timezone
    return format(date, formatString);
  } catch (error) {
    console.error('Error formatting date:', error, 'Input:', utcTimestamp);
    return '-';
  }
}

/**
 * Formats a UTC timestamp with seconds precision.
 * 
 * @param utcTimestamp - ISO 8601 UTC timestamp string
 * @returns Formatted date-time string with seconds
 * 
 * @example
 * formatDateTimeWithSeconds("2024-01-15T14:30:45.000Z") // "Jan 15, 2024 14:30:45"
 */
export function formatDateTimeWithSeconds(utcTimestamp: string | null | undefined): string {
  return formatDateTime(utcTimestamp, DATETIME_WITH_SECONDS_FORMAT);
}

/**
 * Formats a UTC timestamp to date only (no time).
 * 
 * @param utcTimestamp - ISO 8601 UTC timestamp string
 * @returns Formatted date string
 * 
 * @example
 * formatDateOnly("2024-01-15T14:30:00.000Z") // "Jan 15, 2024"
 */
export function formatDateOnly(utcTimestamp: string | null | undefined): string {
  return formatDateTime(utcTimestamp, DATE_ONLY_FORMAT);
}

/**
 * Formats a UTC timestamp to time only (no date).
 * 
 * @param utcTimestamp - ISO 8601 UTC timestamp string
 * @returns Formatted time string
 * 
 * @example
 * formatTimeOnly("2024-01-15T14:30:00.000Z") // "14:30"
 */
export function formatTimeOnly(utcTimestamp: string | null | undefined): string {
  return formatDateTime(utcTimestamp, TIME_ONLY_FORMAT);
}

/**
 * Formats a UTC timestamp with full month name.
 * 
 * @param utcTimestamp - ISO 8601 UTC timestamp string
 * @returns Formatted date-time string with full month name
 * 
 * @example
 * formatFullDateTime("2024-01-15T14:30:00.000Z") // "January 15, 2024 at 14:30"
 */
export function formatFullDateTime(utcTimestamp: string | null | undefined): string {
  return formatDateTime(utcTimestamp, FULL_DATETIME_FORMAT);
}

/**
 * Legacy compatibility function for existing code using toLocaleString pattern.
 * Converts UTC timestamp to local timezone with similar output to toLocaleString.
 * 
 * @param utcTimestamp - ISO 8601 UTC timestamp string
 * @returns Formatted date-time string
 * 
 * @example
 * formatDateTimeLegacy("2024-01-15T14:30:00.000Z") // "Jan 15, 2024 14:30"
 */
export function formatDateTimeLegacy(utcTimestamp: string | null | undefined): string {
  return formatDateTime(utcTimestamp, DEFAULT_DATETIME_FORMAT);
}
